/**
 * useSubscription Hook
 * Manages subscription state and feature gating.
 *
 * Source of truth for paid/unlocked state: RevenueCat customerInfo.
 * Backend /subscription/status is a denormalized cache driven by webhooks;
 * it MAY lag or (rarely) misclassify. When RC says active and backend says
 * FREE, we keep RC's active state and log a warning — the backend will
 * reconcile via webhook + customerInfo listener on the next tick.
 *
 * Note on tier naming:
 *   The customer-facing plan is "Fitxess Pro". Internally we keep the legacy
 *   constant TIERS.PLUS as an alias for the single paid tier to minimize
 *   churn across the codebase.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import subscriptionService, { TIERS, FEATURES, hasPaidEntitlement } from '../services/subscriptionService';
import { subscriptionAPI } from '../utils/api';
import useUserStore from '../store/userStore';
import { resolveInitialEntitlement } from './entitlementResolution';

// Key the "entitlement resolved" marker by account, so a logout/login or
// account switch is unknown again until resolved for the NEW user.
function entitlementKey(userId) {
  return userId || '__anon__';
}

// Default entitlements for free tier
const FREE_ENTITLEMENTS = {
  can_generate_weekly_plan: false,
  can_save_plans: false,
  can_regenerate_unlimited: false,
  can_access_grocery_list: false,
  can_customize_macros: false,
  can_edit_plans: false,
  daily_plan_limit: 0,
};

// Default entitlements for plus tier (single paid tier = Fitxess Pro)
const PLUS_ENTITLEMENTS = {
  can_generate_weekly_plan: true,
  can_save_plans: true,
  can_regenerate_unlimited: true,
  can_access_grocery_list: true,
  can_customize_macros: true,
  can_edit_plans: true,
  daily_plan_limit: -1,
};

export function useSubscription() {
  const { user } = useUserStore();
  // `loading` (exposed) means ONLY "we have never resolved this user's
  // entitlement yet". It is derived from the account this state was resolved
  // for, so background refreshes (RC listener, /subscription/status refresh,
  // Sync, purchase/restore reconcile) can never flip it back to true — that
  // used to make PlanningGate unmount PlanningHub on every refresh.
  const [resolvedFor, setResolvedFor] = useState(null);
  const loading = resolvedFor !== entitlementKey(user?.user_id);
  // Background refresh in flight (informational; never gates the UI).
  const [refreshing, setRefreshing] = useState(false);
  const [tier, setTier] = useState(TIERS.FREE);
  const [entitlements, setEntitlements] = useState(FREE_ENTITLEMENTS);
  const [isActive, setIsActive] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [willRenew, setWillRenew] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [offeringsLoading, setOfferingsLoading] = useState(true);
  const [offeringsError, setOfferingsError] = useState(false);
  // True while we're silently recovering an "already subscribed" situation
  // (via restore / getCustomerInfo). Consumers — notably the Paywall — use
  // this to swap the spinner label to a calm "Confirming your Fitxess Pro
  // access…" message instead of the alarming "Processing…".
  const [isRecovering, setIsRecovering] = useState(false);
  // rcReady: true once Purchases.configure + logIn have both resolved for
  // the currently logged-in user. The Paywall must block purchase() calls
  // until this is true so purchases are never attributed to an anonymous
  // RC App User ID (which would then get "transferred" to the real user
  // and confuse entitlement resolution).
  const [rcReady, setRcReady] = useState(false);

  // Refs mirror the latest tier/isActive so the smart-merge guard inside
  // fetchSubscriptionStatus can read the current RC-applied state without
  // creating a useCallback dependency cycle.
  const tierRef = useRef(tier);
  const isActiveRef = useRef(isActive);
  useEffect(() => { tierRef.current = tier; }, [tier]);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  // Fetch subscription status from backend with a smart-merge guard:
  // if RC has already marked us active/paid locally, a backend FREE
  // response MUST NOT downgrade us. The backend will catch up when the
  // RevenueCat webhook arrives.
  //
  // A BACKGROUND refresh: it never touches `loading`. Resolves (never
  // rejects) to { ok, paid } — `paid` is the local paid state after the merge.
  const fetchSubscriptionStatus = useCallback(async () => {
    const localPaid = () => !!isActiveRef.current && tierRef.current !== TIERS.FREE;
    setRefreshing(true);
    try {
      const response = await subscriptionAPI.getStatus();
      const data = response.data || {};

      const currentlyActivePaid =
        isActiveRef.current && tierRef.current !== TIERS.FREE;
      const backendFree = !data.is_active || data.tier === TIERS.FREE;

      if (currentlyActivePaid && backendFree) {
        console.warn(
          '[useSubscription] Backend reports FREE but RC entitlement is active locally — keeping RC state; backend will reconcile via webhook.'
        );
        // Do NOT overwrite tier/isActive/entitlements. The expires/willRenew
        // values we already have from RC (or a prior backend sync) are fine.
        return { ok: true, paid: true };
      }

      // Mirror into the refs synchronously: RevenueCat startup now runs in
      // parallel and must see this state before the next render commits.
      tierRef.current = data.tier || TIERS.FREE;
      isActiveRef.current = data.is_active || false;
      setTier(data.tier || TIERS.FREE);
      setIsActive(data.is_active || false);
      setExpiresAt(data.expires_at);
      setWillRenew(data.will_renew || false);
      setEntitlements(data.entitlements || FREE_ENTITLEMENTS);

      console.log('[useSubscription] Status:', data);
      return { ok: true, paid: localPaid() };
    } catch (error) {
      console.error('[useSubscription] Failed to fetch status:', error);
      // Do NOT default to free on error if we were already unlocked by RC —
      // an API blip shouldn't relock a paying user.
      if (!isActiveRef.current) {
        tierRef.current = TIERS.FREE;
        setTier(TIERS.FREE);
        setEntitlements(FREE_ENTITLEMENTS);
      }
      return { ok: false, paid: localPaid() };
    } finally {
      setRefreshing(false);
    }
  }, []);

  // Apply a RevenueCat CustomerInfo payload to local state so the UI can
  // unlock premium features immediately after a successful purchase/restore —
  // without waiting for the backend webhook to arrive. The backend status
  // fetch that follows will confirm/reconcile (with the smart-merge guard
  // preventing a stale FREE response from relocking the user).
  //
  // Accepts any recognized paid entitlement identifier including the
  // production "Fitxess Pro" and legacy aliases (pro / plus / premium) via
  // hasPaidEntitlement().
  // Load RevenueCat offerings for the paywall. Prices are shown ONLY from
  // live offerings — on failure we surface an error state (Retry) rather than
  // any stale hard-coded price.
  const reloadOfferings = useCallback(async () => {
    setOfferingsLoading(true);
    setOfferingsError(false);
    try {
      const offs = await subscriptionService.getOfferings();
      const hasPackages = !!(offs && offs.current && Array.isArray(offs.current.availablePackages) && offs.current.availablePackages.length > 0);
      setOfferings(offs);
      setOfferingsError(!hasPackages);
    } catch (error) {
      console.error('[useSubscription] Failed to load offerings:', error);
      setOfferings(null);
      setOfferingsError(true);
    } finally {
      setOfferingsLoading(false);
    }
  }, []);


  const applyCustomerInfo = useCallback((customerInfo) => {
    if (!customerInfo) return false;
    if (!hasPaidEntitlement(customerInfo)) return false;

    const activeEnts = customerInfo?.entitlements?.active || {};

    // Pick the earliest expiration across active entitlements (if any).
    let nextExpiresAt = null;
    let nextWillRenew = false;
    Object.values(activeEnts).forEach((ent) => {
      if (ent?.expirationDate && (!nextExpiresAt || ent.expirationDate < nextExpiresAt)) {
        nextExpiresAt = ent.expirationDate;
      }
      if (ent?.willRenew) nextWillRenew = true;
    });

    // Refs first (synchronously) so a backend FREE response racing this in
    // parallel hits the smart-merge guard instead of relocking the user.
    tierRef.current = TIERS.PLUS;
    isActiveRef.current = true;
    setTier(TIERS.PLUS);
    setIsActive(true);
    setEntitlements(PLUS_ENTITLEMENTS);
    setExpiresAt(nextExpiresAt);
    setWillRenew(nextWillRenew);
    return true;
  }, []);

  // Initialize RevenueCat and resolve the initial entitlement.
  //
  // The backend /subscription/status read starts IMMEDIATELY, in parallel with
  // RevenueCat configure -> logIn (a network call) -> listener -> customerInfo.
  // `loading` clears via resolveInitialEntitlement: as soon as EITHER source
  // confirms paid, or once BOTH have answered not-paid (FREE). Offerings and
  // the fire-and-forget backend reconcile never block it.
  useEffect(() => {
    let listenerId = null;
    let cancelled = false;
    const key = entitlementKey(user?.user_id);
    const native = Capacitor.isNativePlatform();

    // A different account than the one this state was resolved for: start
    // from FREE (still `loading`, so nothing renders it) so a previous
    // account's paid state can never leak through the smart-merge guard.
    if (resolvedFor !== null && resolvedFor !== key) {
      tierRef.current = TIERS.FREE;
      isActiveRef.current = false;
      setTier(TIERS.FREE);
      setIsActive(false);
      setEntitlements(FREE_ENTITLEMENTS);
      setExpiresAt(null);
      setWillRenew(false);
    }

    // 1) Backend status — not queued behind RevenueCat.
    const backendCheck = fetchSubscriptionStatus();

    // 2) RevenueCat startup (native + signed in only).
    let rcCheck = null;
    if (native && user?.user_id) {
      rcCheck = (async () => {
        setRcReady(false);
        const configured = await subscriptionService.initializeRevenueCat(user.user_id);
        let loginInfo = null;
        try {
          const loginResult = await subscriptionService.identifyUser(user.user_id);
          loginInfo = loginResult?.customerInfo || null;
          // Send ONLY the verified email as RC's reserved $email attribute so
          // the customer is searchable by email in the RevenueCat dashboard
          // (needed for granting/revoking promotional "Fitxess Pro").
          await subscriptionService.setUserEmail(user.email);
        } catch (e) {
          console.warn('[useSubscription] identifyUser failed (non-fatal):', e);
        }
        // Only mark RC ready after BOTH configure+identify complete. Until
        // this flips true, the paywall disables Continue to prevent
        // anonymous-then-transfer purchases.
        if (!cancelled && configured) setRcReady(true);
        if (cancelled) return { paid: false };

        // Subscribe to live customerInfo updates so iOS-driven entitlement
        // changes (including the "already subscribed" recovery path) unlock
        // the UI even if purchase() never resolves on this device. A
        // BACKGROUND refresh: it never puts the app back into `loading`.
        listenerId = await subscriptionService.addCustomerInfoUpdateListener(
          (customerInfo) => {
            if (cancelled) return;
            const applied = applyCustomerInfo(customerInfo);
            if (applied) {
              // Reconcile in the background; non-fatal on failure.
              fetchSubscriptionStatus().catch((e) =>
                console.warn('[useSubscription] reconcile after listener failed', e)
              );
            }
          }
        );

        // Trigger a server-side reconcile so the backend's
        // user_subscriptions row is brought into line with RC's
        // authoritative state. This is the only mechanism that can
        // refresh a stale row when an RC webhook is missed.
        // Fire-and-forget: never awaited, never blocks initial resolution.
        subscriptionAPI
          .restore(user.user_id)
          .then((r) => {
            const reconcileStatus = r?.data?.reconcile_status || r?.data?.subscription?.reconcile_status;
            console.log('[SUBSCRIPTION_DIAG] reconcile_status=', reconcileStatus, 'rc_lookup=', r?.data?.rc_lookup, 'backend_tier=', r?.data?.subscription?.tier, 'is_active=', r?.data?.subscription?.is_active);
          })
          .catch((e) => {
            console.warn('[useSubscription] boot reconcile failed (non-fatal):', e?.message || e);
          });

        if (!configured) return { paid: false };

        // Launch customerInfo read (silent, read-only). Catches stale device
        // state (e.g. user was already subscribed but never refreshed) and
        // unlocks the app. Also RevenueCat's answer for initial resolution.
        let paid = !cancelled && applyCustomerInfo(loginInfo);
        try {
          const infoResult = await subscriptionService.getCustomerInfo();
          if (cancelled) return { paid: false };
          const customerInfo = infoResult?.customerInfo || infoResult;
          paid = applyCustomerInfo(customerInfo) || paid;

          // === RC ↔ backend parity diagnostic (TestFlight-friendly) ===
          // Single boot log: RC appUserID, backend user_id, active
          // entitlement keys. Lets us confirm whether frontend and backend
          // are looking at the same user when debugging subscription issues.
          try {
            const rcAppUserId = customerInfo?.originalAppUserId || customerInfo?.original_app_user_id || null;
            const activeEntKeys = Object.keys(customerInfo?.entitlements?.active || {});
            console.log('[SUBSCRIPTION_DIAG]', JSON.stringify({
              backend_user_id: user.user_id,
              rc_app_user_id: rcAppUserId,
              rc_active_entitlements: activeEntKeys,
              ids_match: rcAppUserId === user.user_id,
            }));
          } catch (e) {
            // diagnostic logging must never break boot
          }
        } catch (e) {
          console.log('[useSubscription] silent launch sync skipped:', e?.message || e);
        }
        return { paid };
      })().catch((e) => {
        console.warn('[useSubscription] RevenueCat startup failed (non-fatal):', e);
        return { paid: false };
      });
    } else {
      // Web / no user yet — RC is not applicable here, but we still mark
      // "ready" so non-native UI doesn't sit in a disabled state forever.
      setRcReady(!native);
    }

    // 3) Initial resolution — `loading` clears here and only here.
    resolveInitialEntitlement(backendCheck, rcCheck).then((r) => {
      if (cancelled) return;
      console.log('[useSubscription] initial entitlement resolved:', r.source);
      setResolvedFor(key);
    });

    // 4) Offerings are for the paywall only — load in the background (after
    // RevenueCat is configured on native), never blocking normal app UI.
    Promise.resolve(rcCheck).then(() => {
      if (!cancelled) reloadOfferings();
    });

    return () => {
      cancelled = true;
      if (listenerId) {
        subscriptionService.removeCustomerInfoUpdateListener(listenerId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, fetchSubscriptionStatus, applyCustomerInfo]);

  // Check if user can access a specific feature
  const canAccess = useCallback((feature) => {
    return entitlements[feature] === true;
  }, [entitlements]);

  // Check if user is subscribed (plus or higher)
  const isSubscribed = useCallback(() => {
    return tier !== TIERS.FREE && isActive;
  }, [tier, isActive]);

  // Check if user is on free tier
  const isFree = useCallback(() => {
    return tier === TIERS.FREE || !isActive;
  }, [tier, isActive]);

  // Pull the latest CustomerInfo from RevenueCat and apply it locally.
  // Used by the paywall as a watchdog/safety net when iOS shows the
  // "already subscribed" alert and the purchase() promise hangs.
  const syncCustomerInfo = useCallback(async () => {
    setIsRecovering(true);
    try {
      const result = await subscriptionService.getCustomerInfo();
      const customerInfo = result?.customerInfo || result;
      const applied = applyCustomerInfo(customerInfo);
      if (applied) {
        try {
          await fetchSubscriptionStatus();
        } catch (e) {
          console.warn('[useSubscription] reconcile after syncCustomerInfo failed', e);
        }
      }
      return { applied, customerInfo };
    } catch (e) {
      console.warn('[useSubscription] syncCustomerInfo failed', e);
      return { applied: false, customerInfo: null };
    } finally {
      setIsRecovering(false);
    }
  }, [applyCustomerInfo, fetchSubscriptionStatus]);

  // Purchase a subscription.
  // Returns { customerInfo, applied, recovered? } so the Paywall can gate
  // the "Welcome" toast on confirmed active entitlement rather than raw
  // StoreKit success.
  const purchase = useCallback(async (packageToPurchase) => {
    try {
      const result = await subscriptionService.purchasePackage(packageToPurchase);

      // 1. Confirm the purchase actually surfaced an active entitlement.
      const applied = applyCustomerInfo(result?.customerInfo);

      // 2. Bring the backend row in line with RevenueCat NOW (fresh lookup,
      //    never the 60s cache primed by the launch sync) and wait for it, so
      //    the status read below sees the reconciled row. Safe to fail.
      if (applied && user?.user_id) {
        try {
          const r = await subscriptionAPI.restore(user.user_id, { forceRefresh: true });
          console.log('[SUBSCRIPTION_DIAG] purchase reconcile_status=', r?.data?.reconcile_status, 'rc_lookup=', r?.data?.rc_lookup);
        } catch (e) {
          console.warn('[useSubscription] backend restore after purchase failed', e);
        }
      }

      // 3. Reconcile with backend. Smart-merge prevents a stale FREE
      //    response from relocking an already-unlocked user.
      try {
        await fetchSubscriptionStatus();
      } catch (e) {
        console.warn('[useSubscription] backend reconcile failed after purchase', e);
      }

      return { customerInfo: result?.customerInfo, applied };
    } catch (error) {
      // Apple says "You're currently subscribed to this." StoreKit refuses
      // to issue a new transaction, so we recover the existing entitlement
      // via restore + customerInfo refresh and treat it as success.
      if (subscriptionService.isAlreadySubscribedError(error)) {
        console.log(
          '[useSubscription] Already-subscribed detected — recovering via restore'
        );
        setIsRecovering(true);
        try {
          const restoreResult = await subscriptionService.restorePurchases();
          let customerInfo = restoreResult?.customerInfo;
          let applied = applyCustomerInfo(customerInfo);

          // If restore didn't surface entitlements, ask for fresh info.
          if (!applied) {
            const infoResult = await subscriptionService.getCustomerInfo();
            customerInfo = infoResult?.customerInfo || infoResult;
            applied = applyCustomerInfo(customerInfo);
          }

          // Sync with backend (best-effort).
          if (user?.user_id) {
            try {
              await subscriptionAPI.restore(user.user_id, { forceRefresh: true });
            } catch (e) {
              console.warn('[useSubscription] backend restore sync failed', e);
            }
          }
          try {
            await fetchSubscriptionStatus();
          } catch (e) {
            console.warn('[useSubscription] reconcile after recovery failed', e);
          }

          if (applied) {
            return { customerInfo, applied: true, recovered: true };
          }
          return { customerInfo, applied: false, recovered: true };
        } catch (recoverError) {
          console.error(
            '[useSubscription] Recovery from already-subscribed failed',
            recoverError
          );
        } finally {
          setIsRecovering(false);
        }
      }
      throw error;
    }
  }, [fetchSubscriptionStatus, applyCustomerInfo, user?.user_id]);

  // Restore purchases. Returns { customerInfo, applied } so callers can
  // gate UI on confirmed active entitlement, matching purchase().
  const restore = useCallback(async () => {
    const result = await subscriptionService.restorePurchases();
    const applied = applyCustomerInfo(result?.customerInfo);

    // Sync with backend
    if (user?.user_id) {
      try {
        await subscriptionAPI.restore(user.user_id, { forceRefresh: true });
      } catch (e) {
        console.warn('[useSubscription] backend restore sync failed', e);
      }
    }

    // Reconcile status (smart-merge prevents relock)
    try {
      await fetchSubscriptionStatus();
    } catch (e) {
      console.warn('[useSubscription] backend reconcile failed after restore', e);
    }

    return { customerInfo: result?.customerInfo, applied };
  }, [user?.user_id, fetchSubscriptionStatus, applyCustomerInfo]);

  // Open subscription management
  const manageSubscription = useCallback(() => {
    subscriptionService.openSubscriptionManagement();
  }, []);


  // Force the server to reconcile this user's subscription against RC's
  // authoritative state and return the result. Does NOT touch the device-
  // side RC SDK (no Apple confirmation dialog, no UI prompts). Used by:
  //   • the global 402 handler when frontend thinks "Pro" but backend
  //     returned 402 (split-brain recovery)
  //   • the Settings "Sync Subscription" button
  //
  // Returns the backend's response shape:
  //   { tier, is_active, expires_at, ..., reconcile_status }
  //
  // Possible reconcile_status values:
  //   "ok_paid"           — RC has active paid entitlement; backend row upserted
  //   "ok_free"           — RC has no active entitlement; backend row downgraded
  //   "skipped_override"  — admin premium_override row preserved
  //   "degraded:<reason>" — RC unreachable; DB not mutated
  const reconcileBackend = useCallback(async () => {
    if (!user?.user_id) {
      return { reconcile_status: 'no_user', is_active: false, tier: TIERS.FREE };
    }
    try {
      const res = await subscriptionAPI.restore(user.user_id, { forceRefresh: true });
      const sub = res?.data?.subscription || {};
      const status = sub.reconcile_status || res?.data?.reconcile_status || 'unknown';
      console.log('[useSubscription] reconcileBackend ->', status, 'rc_lookup=', res?.data?.rc_lookup, 'tier=', sub.tier, 'is_active=', sub.is_active);
      return { ...sub, reconcile_status: status };
    } catch (e) {
      console.warn('[useSubscription] reconcileBackend failed:', e?.message || e);
      return { reconcile_status: 'request_failed', is_active: false, tier: TIERS.FREE };
    }
  }, [user?.user_id]);

  // Explicitly downgrade local state to free, overriding the smart-merge
  // guard. Called by the 402 handler ONLY after the backend has been
  // reconciled against RC and confirmed `ok_free` — at that point the
  // device-side cached customerInfo is stale and trying to keep the user
  // "subscribed" locally would lock them into the broken split-brain.
  const forceDowngradeToFree = useCallback(() => {
    console.warn('[useSubscription] forceDowngradeToFree — backend reconcile confirmed no active entitlement');
    tierRef.current = TIERS.FREE;
    isActiveRef.current = false;
    setTier(TIERS.FREE);
    setIsActive(false);
    setExpiresAt(null);
    setWillRenew(false);
    setEntitlements(FREE_ENTITLEMENTS);
  }, []);

  return {
    // State
    userId: user?.user_id || null,
    // true ONLY until this user's entitlement is first resolved.
    loading,
    // a background subscription refresh is in flight (never gates UI).
    refreshing,
    tier,
    entitlements,
    isActive,
    expiresAt,
    willRenew,
    offerings,
    offeringsLoading,
    offeringsError,
    isRecovering,
    rcReady,

    // Computed
    isSubscribed: isSubscribed(),
    isFree: isFree(),

    // Methods
    canAccess,
    purchase,
    restore,
    manageSubscription,
    reloadOfferings,
    refresh: fetchSubscriptionStatus,
    syncCustomerInfo,
    reconcileBackend,
    forceDowngradeToFree,

    // Constants
    TIERS,
    FEATURES,
  };
}

export default useSubscription;
